/**
 * @fileoverview Syndicator Showdown — Referral / Invite Friends Service
 * @module services/referralService
 *
 * Handles the "Invite Friends" referral loop:
 *   1. A player gets (or generates) a stable referralCode → user_referrals/{code}
 *   2. A visitor lands on /share/invite/:code → click is tracked, code is
 *      cached locally so it survives the sign-up flow
 *   3. On sign-up, the stored code is resolved back to the inviter's uid and
 *      attached to the new user document, and the inviter's signup counter
 *      ticks up.
 *
 * Deliberately simple for Phase 1 — no reward/credit logic, just tracking
 * (see docs/FirestoreSchema.md for the user_referrals shape and
 * firestore.rules for the scoped public-write rules this relies on).
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  serverTimestamp,
} from 'firebase/firestore';

import { db } from './firebase';

const STORAGE_KEY = 'ss_referral_code';

// ─── Local storage of a visited referral code ─────────────────────────────────

/**
 * Persists the referral code from a visited /share/invite/:code link so it
 * survives navigation into the sign-up flow. Uses both localStorage (survives
 * tab close) and sessionStorage (works even if the user has 3rd-party/
 * persistent storage disabled for this tab).
 *
 * @param {string} code
 */
export function storeReferralCode(code) {
  if (!code) return;
  try {
    localStorage.setItem(STORAGE_KEY, code);
    sessionStorage.setItem(STORAGE_KEY, code);
  } catch (err) {
    console.warn('[referralService] storeReferralCode failed:', err.message);
  }
}

/** @returns {string|null} */
export function getStoredReferralCode() {
  try {
    return localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function clearStoredReferralCode() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Non-fatal — worst case the same code is reused on the next sign-up
  }
}

// ─── Code generation ──────────────────────────────────────────────────────────

function slugFromName(name) {
  return (name || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
}

function randomSuffix() {
  return String(Math.floor(100 + Math.random() * 900)); // 3 digits
}

/**
 * Returns the signed-in player's referral code, generating and persisting a
 * new one the first time they open the invite card. Idempotent (safe to call
 * on every Dashboard mount) — reuses `userDoc.referralCode` once it exists.
 *
 * @param {import('firebase/auth').User} user
 * @param {Object|null} userDoc - the caller's own users/{uid} document data
 * @returns {Promise<string>}
 */
export async function getOrCreateReferralCode(user, userDoc) {
  if (userDoc?.referralCode) return userDoc.referralCode;

  const base = slugFromName(userDoc?.username || userDoc?.displayName || user.displayName) || 'PLAYER';
  let code = `${base}${randomSuffix()}`;

  // Collision is extremely unlikely (10-char slug + 3 random digits) but the
  // registry is small enough early on that a couple of retries costs nothing.
  for (let attempt = 0; attempt < 3; attempt++) {
    // eslint-disable-next-line no-await-in-loop
    const existing = await getDoc(doc(db, 'user_referrals', code));
    if (!existing.exists()) break;
    code = `${base}${randomSuffix()}`;
  }

  await setDoc(doc(db, 'user_referrals', code), {
    referralCode:  code,
    ownerUid:      user.uid,
    createdAt:     serverTimestamp(),
    clicks:        0,
    signups:       0,
    lastClickedAt: null,
  });

  await updateDoc(doc(db, 'users', user.uid), { referralCode: code });

  return code;
}

// ─── Public reads / tracking ──────────────────────────────────────────────────

/**
 * Public — fetches the inviter's basic public info for the invite landing
 * page. Returns null if the code doesn't exist (never throws).
 *
 * @param {string} referralCode
 * @returns {Promise<{ referralCode: string, ownerUid: string, ownerDisplayName: string|null }|null>}
 */
export async function fetchReferralOwner(referralCode) {
  if (!referralCode) return null;
  try {
    const snap = await getDoc(doc(db, 'user_referrals', referralCode));
    if (!snap.exists()) return null;
    const data = snap.data();

    let ownerDisplayName = null;
    try {
      const profileSnap = await getDoc(doc(db, 'player_profiles', data.ownerUid));
      if (profileSnap.exists()) {
        // Stable Name (username) is the public identity — never expose the
        // inviter's real displayName on this public invite landing page.
        ownerDisplayName = profileSnap.data().username || profileSnap.data().displayName || null;
      }
    } catch {
      // player_profiles is public read, but tolerate any hiccup gracefully —
      // the invite page still works without the inviter's name.
    }

    return { referralCode, ownerUid: data.ownerUid, ownerDisplayName };
  } catch (err) {
    console.error('[referralService] fetchReferralOwner error:', err);
    return null;
  }
}

/**
 * Public — registers a single click on an invite link. Safe for anonymous
 * visitors; the security rules only allow incrementing `clicks` by exactly 1.
 *
 * @param {string} referralCode
 */
export async function trackReferralClick(referralCode) {
  if (!referralCode) return;
  try {
    const ref  = doc(db, 'user_referrals', referralCode);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    await updateDoc(ref, {
      clicks:        increment(1),
      lastClickedAt: serverTimestamp(),
    });
  } catch (err) {
    // Non-fatal vanity metric — never block the invite page over this.
    console.error('[referralService] trackReferralClick error:', err);
  }
}

/**
 * Resolves a stored referral code into the fields needed for a new user
 * document. Called once at sign-up time.
 *
 * @returns {Promise<{ referralCode: string, referredByUid: string }|null>}
 */
export async function resolveStoredReferral() {
  const code = getStoredReferralCode();
  if (!code) return null;
  try {
    const snap = await getDoc(doc(db, 'user_referrals', code));
    if (!snap.exists()) return null;
    return { referralCode: code, referredByUid: snap.data().ownerUid };
  } catch (err) {
    console.error('[referralService] resolveStoredReferral error:', err);
    return null;
  }
}

/**
 * Best-effort — increments the inviter's signup counter once the referred
 * user's account has been created. Never throws — a failure here must not
 * block account creation.
 *
 * @param {string} referralCode
 */
export async function recordReferralSignup(referralCode) {
  if (!referralCode) return;
  try {
    await updateDoc(doc(db, 'user_referrals', referralCode), {
      signups: increment(1),
    });
  } catch (err) {
    console.error('[referralService] recordReferralSignup error:', err);
  }
}
